/**
 * WO-D5 — Your trades (multi-trade selector).
 *
 * Pushed within the Account stack (Account tab → "Your trades"). Loads the GLOBAL trade catalog
 * (GET /api/trades) and the org's current selection (GET /api/org/trades), lets the user toggle any
 * number of trades (none required), and saves via PUT /api/org/trades. The union of selected trades
 * is what shapes the estimate builder's "+ Add service" suggestions (spec §4.1/§4.2).
 *
 * Every control is a real backend round-trip over the @beam/api-types client via TanStack Query,
 * with loading / empty / error states. NO mock data. NEVER imports @smartbox/* (invariant 1).
 */

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAvailableTrades,
  getOrgTrades,
  setOrgTrades,
  type AvailableTrade,
} from '../../../../src/api/services';
import { Screen, Text, Card, Button, ListRow } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';
import type { OrgTradeDto } from '@beam/api-types';

export default function TradesSettingsScreen() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [saved, setSaved] = useState(false);

  const availableQuery = useQuery({
    queryKey: ['available-trades'],
    queryFn: listAvailableTrades,
    staleTime: 5 * 60_000,
  });
  const orgTradesQuery = useQuery({
    queryKey: ['org-trades'],
    queryFn: getOrgTrades,
    staleTime: 30_000,
  });

  // Seed the local selection from the saved org trades once they load.
  useEffect(() => {
    if (orgTradesQuery.data && selected === null) {
      setSelected(new Set(orgTradesQuery.data.map((t: OrgTradeDto) => t.tradeId)));
    }
  }, [orgTradesQuery.data, selected]);

  const saveMutation = useMutation({
    mutationFn: (ids: string[]) => setOrgTrades(ids),
    onSuccess: (trades) => {
      queryClient.setQueryData(['org-trades'], trades);
      // Suggestions depend on the trade union — invalidate so the picker re-ranks.
      void queryClient.invalidateQueries({ queryKey: ['suggested-services'] });
      void queryClient.invalidateQueries({ queryKey: ['suggested-materials'] });
      setSelected(new Set(trades.map((t: OrgTradeDto) => t.tradeId)));
      setSaved(true);
    },
  });

  const toggle = (id: string) => {
    setSaved(false);
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const savedIds = useMemo(
    () => new Set((orgTradesQuery.data ?? []).map((t: OrgTradeDto) => t.tradeId)),
    [orgTradesQuery.data],
  );
  const dirty = useMemo(() => {
    if (selected === null) return false;
    if (selected.size !== savedIds.size) return true;
    for (const id of selected) if (!savedIds.has(id)) return true;
    return false;
  }, [selected, savedIds]);

  const loading = availableQuery.isLoading || orgTradesQuery.isLoading;
  const errored = availableQuery.isError || orgTradesQuery.isError;
  const trades: AvailableTrade[] = availableQuery.data ?? [];

  return (
    <Screen scroll>
      <Text variant="title">Your trades</Text>
      <Text variant="body" color="secondary">
        Pick the trades you work in. We'll use them to suggest the services you estimate most. Choose
        as many as you like, or none.
      </Text>

      {loading ? (
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading trades"
          />
        </View>
      ) : errored ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(
              availableQuery.error ?? orgTradesQuery.error,
              'Could not load trades.',
            )}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => {
              void availableQuery.refetch();
              void orgTradesQuery.refetch();
            }}
          />
        </Card>
      ) : trades.length === 0 ? (
        <Card>
          <Text variant="body" color="secondary">
            No trades are available to select yet.
          </Text>
        </Card>
      ) : (
        <View style={styles.list}>
          {trades.map((t) => {
            const isOn = selected?.has(t.id) ?? false;
            return (
              <ListRow
                key={t.id}
                title={t.name}
                subtitle={t.code}
                right={
                  <Text variant="label" color={isOn ? 'link' : 'secondary'}>
                    {isOn ? 'Selected' : 'Tap to add'}
                  </Text>
                }
                onPress={() => toggle(t.id)}
                style={isOn ? styles.selectedRow : undefined}
                accessibilityLabel={`${isOn ? 'Remove' : 'Add'} ${t.name}`}
              />
            );
          })}
        </View>
      )}

      {saveMutation.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(saveMutation.error, 'Could not save your trades.')}
          </Text>
        </Card>
      ) : null}

      {saved && !dirty && !saveMutation.isPending ? (
        <Text variant="small" color="link" accessibilityRole="alert">
          Saved. Your service suggestions now reflect these trades.
        </Text>
      ) : null}

      {!loading && !errored && trades.length > 0 ? (
        <Button
          title={selected && selected.size > 0 ? 'Save trades' : 'Save (none selected)'}
          onPress={() => saveMutation.mutate(Array.from(selected ?? []))}
          loading={saveMutation.isPending}
          disabled={saveMutation.isPending || !dirty}
          accessibilityLabel="Save selected trades"
        />
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: { paddingVertical: theme.spacing['2xl'], alignItems: 'center' },
  list: { gap: theme.spacing.sm },
  selectedRow: { borderColor: theme.color.action.primary },
});
